/**
 * ConnectionsTab - Modern 3D Knowledge Graph Visualization
 * 
 * Features:
 * - Interactive 3D force-directed graph with labeled nodes
 * - Entity type filtering
 * - Relationship type filtering
 * - Rich entity details sidebar with claims, metrics, and sources
 * - Path highlighting between two entities
 * - Custom node visuals with entity initials and type indicators
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { ConnectionsView, CIG } from '../../../lib/cockpit/schemas'
import * as THREE from 'three'

// Dynamic import for 3D graph to avoid SSR issues
const ForceGraph3D = React.lazy(() => import('react-force-graph-3d'))

// Generate initials from entity name (2-4 chars)
function getEntityInitials(name: string): string {
  if (!name) return '?'
  
  // Handle common company suffixes
  const cleanName = name
    .replace(/\s+(Inc\.?|Corp\.?|LLC|Ltd\.?|Co\.?|Company|Corporation|Incorporated)$/i, '')
    .trim()
  
  const words = cleanName.split(/\s+/)
  
  if (words.length === 1) {
    // Single word: take first 3-4 chars
    return cleanName.substring(0, 4).toUpperCase()
  }
  
  // Multiple words: take first letter of each word (max 4)
  return words
    .slice(0, 4)
    .map(w => w[0])
    .join('')
    .toUpperCase()
}

// Known company domains for logo lookup
const COMPANY_DOMAINS: Record<string, string> = {
  // Tech giants
  'google': 'google.com',
  'alphabet': 'google.com',
  'meta': 'meta.com',
  'meta platforms': 'meta.com',
  'facebook': 'facebook.com',
  'microsoft': 'microsoft.com',
  'apple': 'apple.com',
  'amazon': 'amazon.com',
  'netflix': 'netflix.com',
  'nvidia': 'nvidia.com',
  'tesla': 'tesla.com',
  'openai': 'openai.com',
  'anthropic': 'anthropic.com',
  'ibm': 'ibm.com',
  'intel': 'intel.com',
  'amd': 'amd.com',
  'oracle': 'oracle.com',
  'salesforce': 'salesforce.com',
  'adobe': 'adobe.com',
  'spotify': 'spotify.com',
  'twitter': 'twitter.com',
  'x': 'x.com',
  'uber': 'uber.com',
  'lyft': 'lyft.com',
  'airbnb': 'airbnb.com',
  'snap': 'snap.com',
  'snapchat': 'snapchat.com',
  'pinterest': 'pinterest.com',
  'linkedin': 'linkedin.com',
  'zoom': 'zoom.us',
  'slack': 'slack.com',
  'dropbox': 'dropbox.com',
  'stripe': 'stripe.com',
  'paypal': 'paypal.com',
  'square': 'squareup.com',
  'shopify': 'shopify.com',
  'twilio': 'twilio.com',
  'cloudflare': 'cloudflare.com',
  'datadog': 'datadoghq.com',
  'snowflake': 'snowflake.com',
  'palantir': 'palantir.com',
  'coinbase': 'coinbase.com',
  'robinhood': 'robinhood.com',
  // Finance
  'jpmorgan': 'jpmorganchase.com',
  'jp morgan': 'jpmorganchase.com',
  'goldman sachs': 'goldmansachs.com',
  'morgan stanley': 'morganstanley.com',
  'bank of america': 'bankofamerica.com',
  'wells fargo': 'wellsfargo.com',
  'citigroup': 'citigroup.com',
  'visa': 'visa.com',
  'mastercard': 'mastercard.com',
  'american express': 'americanexpress.com',
  'blackrock': 'blackrock.com',
  'vanguard': 'vanguard.com',
  'fidelity': 'fidelity.com',
  // Healthcare/Pharma
  'pfizer': 'pfizer.com',
  'johnson & johnson': 'jnj.com',
  'moderna': 'modernatx.com',
  'unitedhealth': 'unitedhealthgroup.com',
  'merck': 'merck.com',
  'abbvie': 'abbvie.com',
  'eli lilly': 'lilly.com',
  'bristol-myers squibb': 'bms.com',
  // Retail
  'walmart': 'walmart.com',
  'target': 'target.com',
  'costco': 'costco.com',
  'home depot': 'homedepot.com',
  'starbucks': 'starbucks.com',
  'mcdonald\'s': 'mcdonalds.com',
  'mcdonalds': 'mcdonalds.com',
  'nike': 'nike.com',
  'coca-cola': 'coca-cola.com',
  'pepsi': 'pepsico.com',
  'pepsico': 'pepsico.com',
  // Auto
  'ford': 'ford.com',
  'general motors': 'gm.com',
  'gm': 'gm.com',
  'toyota': 'toyota.com',
  'volkswagen': 'volkswagen.com',
  'bmw': 'bmw.com',
  'mercedes': 'mercedes-benz.com',
  'rivian': 'rivian.com',
  'lucid': 'lucidmotors.com',
  // Aerospace/Defense
  'boeing': 'boeing.com',
  'lockheed martin': 'lockheedmartin.com',
  'raytheon': 'rtx.com',
  'northrop grumman': 'northropgrumman.com',
  'spacex': 'spacex.com',
  // Energy
  'exxon': 'exxonmobil.com',
  'exxonmobil': 'exxonmobil.com',
  'chevron': 'chevron.com',
  'shell': 'shell.com',
  'bp': 'bp.com',
  // Telecom
  'at&t': 'att.com',
  'verizon': 'verizon.com',
  't-mobile': 't-mobile.com',
  'comcast': 'comcast.com',
  // Media
  'disney': 'disney.com',
  'warner bros': 'warnerbros.com',
  'paramount': 'paramount.com',
  'nbc': 'nbcuniversal.com',
  'cnn': 'cnn.com',
  'fox': 'fox.com',
  'new york times': 'nytimes.com',
  'washington post': 'washingtonpost.com',
  'reuters': 'reuters.com',
  'bloomberg': 'bloomberg.com',
  // Government/Regulators
  'sec': 'sec.gov',
  'fda': 'fda.gov',
  'ftc': 'ftc.gov',
  'federal reserve': 'federalreserve.gov',
  'european commission': 'ec.europa.eu',
  'european union': 'europa.eu',
  'eu': 'europa.eu',
}

// Derive domain from company name for logo lookup
function getCompanyDomain(name: string): string | null {
  if (!name) return null
  
  const normalizedName = name.toLowerCase().trim()
  
  // Check known domains first
  if (COMPANY_DOMAINS[normalizedName]) {
    return COMPANY_DOMAINS[normalizedName]
  }
  
  // Check partial matches
  for (const [key, domain] of Object.entries(COMPANY_DOMAINS)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return domain
    }
  }
  
  // Try to derive domain from name (simple heuristic)
  const cleanName = normalizedName
    .replace(/\s+(inc\.?|corp\.?|llc|ltd\.?|co\.?|company|corporation|incorporated)$/i, '')
    .replace(/[^a-z0-9]/g, '')
  
  if (cleanName.length >= 3) {
    return `${cleanName}.com`
  }
  
  return null
}

// Get logo URL using Clearbit or Google Favicon API
function getLogoUrl(name: string, size: number = 128): string | null {
  const domain = getCompanyDomain(name)
  if (!domain) return null
  
  // Use Clearbit for high-quality logos (free tier available)
  return `https://logo.clearbit.com/${domain}`
}

// Cache for loaded logo textures
const logoTextureCache = new Map<string, THREE.Texture | null>()
const loadingLogos = new Set<string>()

interface ConnectionsTabProps {
  view: ConnectionsView
  cig: CIG
  viewMode?: 'mobile' | 'tablet' | 'desktop'
}

// Color palette for entity types
const ENTITY_COLORS: Record<string, string> = {
  organization: '#3b82f6',  // Blue
  person: '#10b981',        // Green
  concept: '#8b5cf6',       // Purple
  event: '#f59e0b',         // Amber
  location: '#ef4444',      // Red
  metric: '#06b6d4',        // Cyan
  product: '#ec4899',       // Pink
  policy: '#6366f1',        // Indigo
  technology: '#f97316',    // Orange
  law_regulation: '#84cc16', // Lime
  dataset: '#14b8a6',       // Teal
  molecule_protein: '#a855f7', // Violet
  default: '#64748b'        // Gray
}

// Relationship type labels for display
const RELATIONSHIP_LABELS: Record<string, string> = {
  funded: 'Funded by',
  sued: 'Sued by',
  regulates: 'Regulates',
  located_in: 'Located in',
  cited: 'Cited by',
  competes_with: 'Competes with',
  causes: 'Causes',
  partners_with: 'Partners with',
  owns: 'Owns',
  employs: 'Employs',
  produces: 'Produces',
  depends_on: 'Depends on',
  opposes: 'Opposes',
  supports_entity: 'Supports',
  related_to: 'Related to',
  OWNS: 'Owns',
  REGULATES: 'Regulates',
  PARTNERS_WITH: 'Partners with',
  COMPETES_WITH: 'Competes with',
  OPERATES_IN: 'Operates in',
  AFFECTS: 'Affects',
  PRODUCES: 'Produces',
  TRADES: 'Trades with',
}


function ConnectionsTab({ view, cig: _cig, viewMode = 'desktop' }: ConnectionsTabProps) {
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [secondSelectedNode, setSecondSelectedNode] = useState<string | null>(null)
  const [pathMode, setPathMode] = useState(false)
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(null)
  const [relationshipTypeFilter, setRelationshipTypeFilter] = useState<string | null>(null)
  const [graphDimensions, setGraphDimensions] = useState({ width: 800, height: 600 })
  const [use3D, setUse3D] = useState(true)
  const [detailTab, setDetailTab] = useState<'overview' | 'claims' | 'sources'>('overview')
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const fgRef = useRef<any>(null)
  
  const isMobile = viewMode === 'mobile'

  // Update graph dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (graphContainerRef.current) {
        const rect = graphContainerRef.current.getBoundingClientRect()
        setGraphDimensions({
          width: rect.width,
          height: Math.max(400, window.innerHeight - 300)
        })
      }
    }
    
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Filter nodes and edges
  const filteredNodes = useMemo(() => {
    if (!entityTypeFilter) return view.nodes
    return view.nodes.filter((n: any) => n.type === entityTypeFilter)
  }, [view.nodes, entityTypeFilter])

  const filteredEdges = useMemo(() => {
    const nodeIds = new Set(filteredNodes.map((n: any) => n.id))
    let edges = view.edges.filter((e: any) => nodeIds.has(e.source) && nodeIds.has(e.target))
    
    if (relationshipTypeFilter) {
      edges = edges.filter((e: any) => e.type === relationshipTypeFilter)
    }
    
    return edges
  }, [view.edges, filteredNodes, relationshipTypeFilter])

  // Find path between two nodes (simple BFS)
  const pathBetweenNodes = useMemo(() => {
    if (!selectedNode || !secondSelectedNode || selectedNode === secondSelectedNode) return null
    
    // Build adjacency list
    const adjacency: Map<string, string[]> = new Map()
    filteredEdges.forEach((edge: any) => {
      if (!adjacency.has(edge.source)) adjacency.set(edge.source, [])
      if (!adjacency.has(edge.target)) adjacency.set(edge.target, [])
      adjacency.get(edge.source)!.push(edge.target)
      adjacency.get(edge.target)!.push(edge.source)
    })
    
    // BFS to find shortest path
    const queue: string[][] = [[selectedNode]]
    const visited = new Set<string>([selectedNode])
    
    while (queue.length > 0) {
      const path = queue.shift()!
      const current = path[path.length - 1]
      
      if (current === secondSelectedNode) {
        return path
      }
      
      const neighbors = adjacency.get(current) || []
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor)
          queue.push([...path, neighbor])
        }
      }
    }
    
    return null
  }, [selectedNode, secondSelectedNode, filteredEdges])

  // Prepare graph data for react-force-graph
  const graphData = useMemo(() => {
    const isOnPath = (nodeId: string) => pathBetweenNodes?.includes(nodeId)
    const isPathEdge = (source: string, target: string) => {
      if (!pathBetweenNodes) return false
      for (let i = 0; i < pathBetweenNodes.length - 1; i++) {
        if ((pathBetweenNodes[i] === source && pathBetweenNodes[i + 1] === target) ||
            (pathBetweenNodes[i] === target && pathBetweenNodes[i + 1] === source)) {
          return true
        }
      }
      return false
    }
    
    return {
      nodes: filteredNodes.map((n: any) => ({
        id: n.id,
        name: n.label,
        type: n.type,
        description: n.description,
        color: isOnPath(n.id) 
          ? '#22c55e' // Highlight path nodes in green
          : (n.id === selectedNode || n.id === secondSelectedNode)
            ? '#fbbf24' // Selected nodes in yellow
            : ENTITY_COLORS[n.type?.toLowerCase()] || ENTITY_COLORS.default,
        val: n.importance ? n.importance * 10 + 5 : 8,
        // Pass enriched data through
        claims: n.claims || [],
        metrics: n.metrics || [],
        sources: n.sources || [],
      })),
      links: filteredEdges.map((e: any) => ({
        source: e.source,
        target: e.target,
        label: e.label,
        type: e.type,
        weight: e.weight || 1,
        description: e.description,
        isPath: isPathEdge(e.source, e.target)
      }))
    }
  }, [filteredNodes, filteredEdges, selectedNode, secondSelectedNode, pathBetweenNodes])

  // Get selected node details
  const selectedNodeData = selectedNode 
    ? view.nodes.find((n: any) => n.id === selectedNode)
    : null

  const selectedNodeConnections = selectedNode
    ? filteredEdges.filter((e: any) => {
        const sourceId = typeof e.source === 'string' ? e.source : e.source?.id
        const targetId = typeof e.target === 'string' ? e.target : e.target?.id
        return sourceId === selectedNode || targetId === selectedNode
      })
    : []

  // Handle node click
  const handleNodeClick = useCallback((node: any) => {
    if (pathMode && selectedNode && selectedNode !== node.id) {
      setSecondSelectedNode(node.id)
    } else {
      setSelectedNode(node.id)
      setSecondSelectedNode(null)
    }
    setDetailTab('overview')
    
    // Zoom to node
    if (fgRef.current && use3D) {
      const distance = 200
      const distRatio = 1 + distance / Math.hypot(node.x || 0, node.y || 0, node.z || 0)
      fgRef.current.cameraPosition(
        { x: (node.x || 0) * distRatio, y: (node.y || 0) * distRatio, z: (node.z || 0) * distRatio },
        node,
        2000
      )
    }
  }, [use3D, pathMode, selectedNode])

  // Handle background click
  const handleBackgroundClick = useCallback(() => {
    if (!pathMode) {
      setSelectedNode(null)
      setSecondSelectedNode(null)
    }
  }, [pathMode])

  // Toggle path mode
  const togglePathMode = useCallback(() => {
    setPathMode(!pathMode)
    if (pathMode) {
      setSecondSelectedNode(null)
    }
  }, [pathMode])

  // Get confidence display
  const getConfidenceDisplay = (confidence: any) => {
    if (typeof confidence === 'number') {
      return `${Math.round(confidence * 100)}%`
    }
    if (typeof confidence === 'string') {
      return confidence.replace(/_/g, ' ')
    }
    return 'N/A'
  }

  if (view.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-gray-500 dark:text-gray-400">
        <div className="text-center max-w-md mx-auto p-8">
          <svg className="w-20 h-20 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-lg font-medium mb-2">No Entities Detected</h3>
          <p className="text-sm">
            The research pipeline didn't extract any entities or relationships from the sources.
            This could indicate a topic that doesn't involve named organizations, people, or concepts.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col lg:flex-row">
      {/* Main graph area */}
      <div 
        ref={graphContainerRef}
        className="flex-1 relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden rounded-lg"
      >
        {/* Controls overlay */}
        <div className="absolute top-4 left-4 z-10 flex flex-wrap gap-2">
          <select
            value={entityTypeFilter || ''}
            onChange={e => setEntityTypeFilter(e.target.value || null)}
            className="px-3 py-1.5 text-sm bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white"
          >
            <option value="" className="text-gray-900">All entities ({view.nodes.length})</option>
            {view.entity_types.map((type: string) => (
              <option key={type} value={type} className="text-gray-900">
                {type} ({view.nodes.filter((n: any) => n.type === type).length})
              </option>
            ))}
          </select>
          
          <select
            value={relationshipTypeFilter || ''}
            onChange={e => setRelationshipTypeFilter(e.target.value || null)}
            className="px-3 py-1.5 text-sm bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white"
          >
            <option value="" className="text-gray-900">All relationships ({view.edges.length})</option>
            {view.relationship_types.map((type: string) => (
              <option key={type} value={type} className="text-gray-900">
                {RELATIONSHIP_LABELS[type] || type} ({view.edges.filter((e: any) => e.type === type).length})
              </option>
            ))}
          </select>
          
          {/* Path mode toggle */}
          <button
            onClick={togglePathMode}
            className={`
              px-3 py-1.5 text-sm backdrop-blur-sm border rounded-lg flex items-center gap-1.5 transition-colors
              ${pathMode 
                ? 'bg-green-500/20 border-green-400/50 text-green-300' 
                : 'bg-white/10 border-white/20 text-white/70 hover:text-white'}
            `}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            {pathMode ? 'Exit Path Mode' : 'Find Path'}
          </button>
        </div>

        {/* Stats overlay */}
        <div className="absolute top-4 right-4 z-10 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg text-white/80 text-xs font-mono">
          {filteredNodes.length} nodes • {filteredEdges.length} links
        </div>

        {/* Path mode hint */}
        {pathMode && (
          <div className="absolute top-16 left-4 right-4 z-10 px-4 py-2 bg-green-500/20 backdrop-blur-sm rounded-lg border border-green-400/30">
            <p className="text-sm text-green-200">
              {!selectedNode 
                ? 'Click on the first entity to start the path'
                : !secondSelectedNode
                  ? 'Now click on the second entity to see the connection path'
                  : pathBetweenNodes
                    ? `Path found: ${pathBetweenNodes.length} steps`
                    : 'No direct path found between these entities'}
            </p>
          </div>
        )}

        {/* 3D Graph */}
        <React.Suspense 
          fallback={
            <div className="flex items-center justify-center h-full">
              <div className="text-white">Loading 3D visualization...</div>
            </div>
          }
        >
          {use3D && (
            <ForceGraph3D
              ref={fgRef}
              graphData={graphData}
              width={graphDimensions.width}
              height={graphDimensions.height}
              backgroundColor="rgba(0,0,0,0)"
              nodeLabel={(node: any) => `<div class="p-2 bg-gray-900/95 backdrop-blur rounded-lg text-white text-sm shadow-lg border border-white/10">
                <div class="font-semibold">${node.name}</div>
                <div class="text-gray-400 text-xs mt-0.5 capitalize">${node.type || 'entity'}</div>
              </div>`}
              nodeThreeObject={(node: any) => {
                // Create a group to hold the node visuals
                const group = new THREE.Group()
                
                // Get node properties
                const color = node.color || ENTITY_COLORS.default
                const entityName = node.name || 'Unknown'
                const nodeSize = Math.max(8, (node.val || 8))
                
                // Create the main sphere with a nice material
                const sphereGeometry = new THREE.SphereGeometry(nodeSize * 0.6, 32, 32)
                const sphereMaterial = new THREE.MeshPhongMaterial({
                  color: new THREE.Color(color),
                  transparent: true,
                  opacity: 0.9,
                  shininess: 100,
                })
                const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial)
                group.add(sphere)
                
                // Create text label sprite with entity name - positioned BELOW sphere
                const createNameSprite = () => {
                  const canvas = document.createElement('canvas')
                  const ctx = canvas.getContext('2d')
                  if (!ctx) return
                  
                  // Canvas size - wide to accommodate text
                  const width = 512
                  const height = 96
                  canvas.width = width
                  canvas.height = height
                  
                  // Draw dark rounded rectangle background with colored border
                  const padding = 8
                  const cornerRadius = 16
                  
                  // Dark background
                  ctx.fillStyle = 'rgba(15, 23, 42, 0.95)'
                  ctx.beginPath()
                  ctx.roundRect(padding, padding, width - padding * 2, height - padding * 2, cornerRadius)
                  ctx.fill()
                  
                  // Colored border
                  ctx.strokeStyle = color
                  ctx.lineWidth = 3
                  ctx.beginPath()
                  ctx.roundRect(padding, padding, width - padding * 2, height - padding * 2, cornerRadius)
                  ctx.stroke()
                  
                  // Draw entity name
                  const maxWidth = width - padding * 6
                  let fontSize = 36
                  ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
                  
                  // Reduce font size if text is too wide
                  while (ctx.measureText(entityName).width > maxWidth && fontSize > 14) {
                    fontSize -= 2
                    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
                  }
                  
                  ctx.textAlign = 'center'
                  ctx.textBaseline = 'middle'
                  
                  // White text
                  ctx.fillStyle = '#ffffff'
                  ctx.fillText(entityName, width / 2, height / 2, maxWidth)
                  
                  const texture = new THREE.CanvasTexture(canvas)
                  const spriteMaterial = new THREE.SpriteMaterial({
                    map: texture,
                    transparent: true,
                    depthTest: false, // Always render on top
                    depthWrite: false,
                  })
                  const sprite = new THREE.Sprite(spriteMaterial)
                  // Scale and position BELOW the sphere
                  const labelWidth = Math.max(nodeSize * 3.5, 30)
                  const labelHeight = labelWidth * (height / width)
                  sprite.scale.set(labelWidth, labelHeight, 1)
                  sprite.position.set(0, -(nodeSize * 0.8 + labelHeight / 2), 0) // Below sphere
                  sprite.renderOrder = 999 // Render last (on top)
                  group.add(sprite)
                }
                
                // Create the name label
                createNameSprite()
                
                return group
              }}
              nodeRelSize={6}
              nodeVal={(node: any) => node.val}
              nodeOpacity={0.9}
              linkColor={(link: any) => link.isPath ? 'rgba(34, 197, 94, 0.8)' : 'rgba(255,255,255,0.2)'}
              linkWidth={(link: any) => link.isPath ? 3 : 1}
              linkOpacity={0.6}
              linkDirectionalParticles={(link: any) => link.isPath ? 4 : 2}
              linkDirectionalParticleWidth={(link: any) => link.isPath ? 3 : 1.5}
              linkDirectionalParticleSpeed={0.005}
              linkDirectionalParticleColor={(link: any) => link.isPath ? 'rgba(34, 197, 94, 1)' : 'rgba(100,200,255,0.8)'}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
              enableNodeDrag={true}
              enableNavigationControls={true}
              showNavInfo={false}
            />
          )}
        </React.Suspense>

        {/* 2D Fallback / Toggle */}
        <div className="absolute bottom-4 left-4 z-10">
          <div className="flex gap-1 bg-white/10 backdrop-blur-sm rounded-lg p-1">
            <button
              onClick={() => setUse3D(true)}
              className={`px-3 py-1 text-xs font-medium rounded ${
                use3D
                  ? 'bg-white text-gray-900'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              3D
            </button>
            <button
              onClick={() => setUse3D(false)}
              className={`px-3 py-1 text-xs font-medium rounded ${
                !use3D
                  ? 'bg-white text-gray-900'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              2D List
            </button>
          </div>
        </div>

        {/* 2D List Fallback when 3D is off */}
        {!use3D && (
          <div className="absolute inset-0 overflow-auto p-4 pt-16">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredNodes.map((node: any) => {
                const initials = getEntityInitials(node.label || '')
                const bgColor = ENTITY_COLORS[node.type?.toLowerCase()] || ENTITY_COLORS.default
                const logoUrl = getLogoUrl(node.label)
                
                return (
                  <button
                    key={node.id}
                    onClick={() => handleNodeClick(node)}
                    className={`
                      p-4 rounded-xl text-left transition-all group
                      ${selectedNode === node.id
                        ? 'bg-blue-500/30 border-blue-400 ring-2 ring-blue-400/50'
                        : secondSelectedNode === node.id
                          ? 'bg-green-500/30 border-green-400 ring-2 ring-green-400/50'
                          : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/30'
                      }
                      border backdrop-blur-sm
                    `}
                  >
                    {/* Entity avatar with logo or initials */}
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden"
                        style={{ 
                          backgroundColor: bgColor,
                          boxShadow: `0 4px 12px ${bgColor}40`
                        }}
                      >
                        {logoUrl ? (
                          <img 
                            src={logoUrl} 
                            alt={node.label}
                            className="w-8 h-8 object-contain bg-white rounded"
                            onError={(e) => {
                              // Hide image on error, show initials
                              e.currentTarget.style.display = 'none'
                              const parent = e.currentTarget.parentElement
                              if (parent) {
                                const span = document.createElement('span')
                                span.className = 'text-white font-bold text-sm'
                                span.textContent = initials
                                parent.appendChild(span)
                              }
                            }}
                          />
                        ) : (
                          <span className="text-white font-bold text-sm">
                            {initials}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate group-hover:text-blue-300 transition-colors">
                          {node.label}
                        </div>
                        <div className="text-xs text-white/60 capitalize mt-0.5">
                          {node.type}
                        </div>
                        {(node.claims?.length > 0 || node.sources?.length > 0) && (
                          <div className="flex items-center gap-2 mt-1.5 text-xs">
                            {node.claims?.length > 0 && (
                              <span className="text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded">
                                {node.claims.length} claims
                              </span>
                            )}
                            {node.sources?.length > 0 && (
                              <span className="text-green-300 bg-green-500/20 px-1.5 py-0.5 rounded">
                                {node.sources.length} sources
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 right-4 z-10 flex flex-wrap gap-2 max-w-sm">
          {view.entity_types.slice(0, 6).map((type: string) => (
            <div 
              key={type} 
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg text-xs text-white/80 border border-white/5"
            >
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: ENTITY_COLORS[type?.toLowerCase()] || ENTITY_COLORS.default }}
              />
              <span className="capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Entity detail drawer */}
      {selectedNodeData && (
        <div className={`
          ${isMobile ? 'absolute inset-0 z-20' : 'w-96 flex-shrink-0'}
          bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col
        `}>
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Entity avatar with logo or initials */}
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden"
                    style={{ 
                      backgroundColor: ENTITY_COLORS[selectedNodeData.type?.toLowerCase()] || ENTITY_COLORS.default,
                      boxShadow: `0 4px 12px ${ENTITY_COLORS[selectedNodeData.type?.toLowerCase()] || ENTITY_COLORS.default}40`
                    }}
                  >
                    {getLogoUrl(selectedNodeData.label) ? (
                      <img 
                        src={getLogoUrl(selectedNodeData.label) || ''}
                        alt={selectedNodeData.label}
                        className="w-8 h-8 object-contain bg-white rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          const parent = e.currentTarget.parentElement
                          if (parent) {
                            const span = document.createElement('span')
                            span.className = 'text-white font-bold text-sm'
                            span.textContent = getEntityInitials(selectedNodeData.label || '')
                            parent.appendChild(span)
                          }
                        }}
                      />
                    ) : (
                      <span className="text-white font-bold text-sm">
                        {getEntityInitials(selectedNodeData.label || '')}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {selectedNodeData.label}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {selectedNodeData.type}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedNode(null)
                    setSecondSelectedNode(null)
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Detail tabs */}
            <div className="flex px-4 border-t border-gray-100 dark:border-gray-700">
              {(['overview', 'claims', 'sources'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setDetailTab(tab)}
                  className={`
                    px-3 py-2 text-xs font-medium capitalize border-b-2 transition-colors
                    ${detailTab === tab 
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}
                  `}
                >
                  {tab}
                  {tab === 'claims' && (selectedNodeData.claims?.length ?? 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-400">({selectedNodeData.claims?.length ?? 0})</span>
                  )}
                  {tab === 'sources' && (selectedNodeData.sources?.length ?? 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-400">({selectedNodeData.sources?.length ?? 0})</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Detail content */}
          <div className="flex-1 overflow-y-auto p-4">
            {detailTab === 'overview' && (
              <div className="space-y-4">
                {/* Description */}
                {selectedNodeData.description && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      Description
                    </h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {selectedNodeData.description}
                    </p>
                  </div>
                )}

                {/* Importance */}
                {selectedNodeData.importance !== undefined && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      Relevance Score
                    </h4>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${(selectedNodeData.importance || 0.5) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {Math.round((selectedNodeData.importance || 0.5) * 100)}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Related Metrics */}
                {selectedNodeData.metrics && selectedNodeData.metrics.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      Related Metrics
                    </h4>
                    <div className="space-y-2">
                      {selectedNodeData.metrics.slice(0, 5).map((metric: any, idx: number) => (
                        <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {metric.label}
                            </span>
                            {metric.trend && (
                              <span className={`text-xs ${
                                metric.trend === 'up' ? 'text-green-500' :
                                metric.trend === 'down' ? 'text-red-500' :
                                'text-gray-500'
                              }`}>
                                {metric.trend === 'up' ? '↑' : metric.trend === 'down' ? '↓' : '→'}
                              </span>
                            )}
                          </div>
                          <div className="text-lg font-semibold text-gray-900 dark:text-white">
                            {typeof metric.value === 'object' 
                              ? metric.value.formatted || metric.value.value 
                              : metric.value}
                            {metric.unit && <span className="text-sm text-gray-500 ml-1">{metric.unit}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Connections */}
                {selectedNodeConnections.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      Connections ({selectedNodeConnections.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedNodeConnections.slice(0, 10).map((edge: any) => {
                        const sourceId = typeof edge.source === 'string' ? edge.source : edge.source?.id
                        const targetId = typeof edge.target === 'string' ? edge.target : edge.target?.id
                        const isSource = sourceId === selectedNode
                        const connectedNodeId = isSource ? targetId : sourceId
                        const connectedNode = view.nodes.find((n: any) => n.id === connectedNodeId)
                        
                        if (!connectedNode) return null
                        
                        return (
                          <button
                            key={edge.id}
                            onClick={() => setSelectedNode(connectedNodeId)}
                            className="w-full p-3 text-left bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                              <span className={`font-medium ${isSource ? 'text-blue-500' : 'text-purple-500'}`}>
                                {isSource ? '→' : '←'}
                              </span>
                              <span className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs font-medium">
                                {RELATIONSHIP_LABELS[edge.type] || edge.label || edge.type || 'related'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: ENTITY_COLORS[connectedNode.type?.toLowerCase()] || ENTITY_COLORS.default }}
                              />
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {connectedNode.label}
                              </span>
                            </div>
                            {edge.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                {edge.description}
                              </p>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedNodeData.tags && selectedNodeData.tags.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedNodeData.tags.map((tag: string, idx: number) => (
                        <span 
                          key={idx}
                          className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'claims' && (
              <div className="space-y-3">
                {selectedNodeData.claims && selectedNodeData.claims.length > 0 ? (
                  selectedNodeData.claims.map((claim: any, idx: number) => (
                    <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30">
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {claim.text}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {claim.category && (
                          <span className="px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded">
                            {claim.category}
                          </span>
                        )}
                        {claim.confidence && (
                          <span className="text-blue-500 dark:text-blue-400">
                            {getConfidenceDisplay(claim.confidence)} confidence
                          </span>
                        )}
                      </div>
                      {claim.anchors && claim.anchors.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-blue-100 dark:border-blue-800/30">
                          <span className="text-xs text-gray-500">
                            {claim.anchors.length} source{claim.anchors.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No claims directly reference this entity</p>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'sources' && (
              <div className="space-y-3">
                {selectedNodeData.sources && selectedNodeData.sources.length > 0 ? (
                  selectedNodeData.sources.map((source: any, idx: number) => (
                    <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          {source.url ? (
                            <a 
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline line-clamp-2"
                            >
                              {source.title}
                            </a>
                          ) : (
                            <span className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                              {source.title}
                            </span>
                          )}
                          {source.snippet && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-3">
                              "{source.snippet}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <p className="text-sm">No sources directly linked to this entity</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ConnectionsTab
